import React, { useEffect, useState } from "react";
import { api, API } from "../lib/api";
import { useAuth } from "../lib/auth";
import VoiceRecorder from "../components/VoiceRecorder";
import { toast } from "sonner";
import { Users, Send, Mic, ShieldAlert } from "lucide-react";

export default function TeacherDashboard() {
  const { user } = useAuth();
  const [students, setStudents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [rating, setRating] = useState(5);
  const [voiceNote, setVoiceNote] = useState("");
  const [sentNotes, setSentNotes] = useState([]);

  useEffect(() => {
    api.get("/teacher/students").then((r) => setStudents(r.data)).catch(() => {});
    api.get("/teacher/voice-notes/sent").then((r) => setSentNotes(r.data)).catch(() => {});
  }, []);

  const send = async () => {
    if (!selected || !note.trim()) return toast.error("Pick student & write a note");
    try {
      await api.post("/teacher/feedback", { student_id: selected.user_id, note, rating });
      toast.success("Feedback sent");
      setNote("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send"); }
  };

  const sendVoice = async (blob, mime) => {
    if (!selected) { toast.error("Pick a student first"); throw new Error("no student"); }
    const fd = new FormData();
    fd.append("audio", blob, `voice.${(mime || "audio/webm").split("/")[1].split(";")[0]}`);
    fd.append("student_id", selected.user_id);
    fd.append("note", voiceNote);
    const r = await fetch(`${API}/teacher/voice-note`, {
      method: "POST", body: fd, credentials: "include",
    });
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      toast.error(d?.detail || "Voice send failed"); throw new Error("fail");
    }
    toast.success("Voice note sent");
    setVoiceNote("");
    const updated = await api.get("/teacher/voice-notes/sent").catch(() => ({ data: [] }));
    setSentNotes(updated.data);
  };

  const isPending = user?.role === "mentor" && user?.teacher_status !== "approved";

  return (
    <div className="space-y-8" data-testid="teacher-page">
      <header>
        <div className="text-xs uppercase tracking-[0.22em] text-gold-600 font-semibold">Mentor hub</div>
        <h1 className="mt-1 font-display font-semibold text-4xl text-forest-700">Review your halaqa</h1>
        <p className="mt-2 text-foreground/70 max-w-2xl">Review students' progress, leave notes, and send voice feedback.</p>
      </header>

      {isPending && (
        <div className="rounded-2xl border border-terracotta-500/40 bg-terracotta-500/10 px-5 py-4 flex items-start gap-3" data-testid="pending-verification-banner">
          <div className="h-9 w-9 rounded-full bg-terracotta-500/20 text-terracotta-600 grid place-items-center shrink-0">
            <ShieldAlert className="h-5 w-5" />
          </div>
          <div>
            <div className="font-display font-semibold text-terracotta-700">Pending verification</div>
            <p className="text-sm text-foreground/80 mt-1">
              Your teacher account is awaiting admin approval. You can browse, but creating halaqas, grading submissions, and sending feedback will be enabled once an admin approves you.
            </p>
          </div>
        </div>
      )}

      <div className="grid gap-6">
        <div className="hifz-card">
          <h2 className="font-display font-semibold text-lg text-forest-700">Students</h2>
          {students.length === 0 ? (
            <p className="mt-3 text-sm text-muted-foreground">No students enrolled in your classes yet.</p>
          ) : (
            <ul className="mt-4 space-y-2">
              {students.map((s) => (
                <li key={s.user_id}>
                  <button
                    onClick={() => setSelected(s)}
                    data-testid={`select-student-${s.user_id}`}
                    className={`w-full text-left p-3 rounded-xl border transition ${
                      selected?.user_id === s.user_id ? "border-forest-600 bg-forest-600/5" : "border-sand-300 hover:bg-sand-50"
                    }`}>
                    <div className="font-medium">{s.name}</div>
                    <div className="text-xs text-muted-foreground">{s.total_memorized || 0} ayahs · {s.streak || 0}d streak</div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="space-y-6">
          <div className="hifz-card">
            <h2 className="font-display font-semibold text-lg text-forest-700">Send feedback</h2>
            {!selected ? (
              <div className="mt-6 text-center text-muted-foreground py-12">
                <Users className="h-8 w-8 mx-auto opacity-50" />
                <p className="mt-2 text-sm">Select a student to write feedback</p>
              </div>
            ) : (
              <div className="mt-5 space-y-4">
                <div className="p-4 rounded-xl bg-sand-200/60 border border-sand-300">
                  <div className="font-display font-semibold text-forest-700">{selected.name}</div>
                  <div className="text-xs text-muted-foreground">{selected.email}</div>
                </div>
                <textarea
                  data-testid="feedback-note"
                  value={note} onChange={(e) => setNote(e.target.value)}
                  rows={4} placeholder="Mashallah on yesterday's revision. Focus on madd in ayah 5…"
                  className="w-full rounded-xl border border-sand-300 bg-white px-4 py-3 text-sm"
                />
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Rating</span>
                    {[1,2,3,4,5].map((n) => (
                      <button key={n} onClick={() => setRating(n)} data-testid={`rating-${n}`}
                        className={`h-7 w-7 rounded-full text-sm ${rating >= n ? "bg-gold-500 text-foreground" : "bg-sand-200 text-muted-foreground"}`}>
                        ★
                      </button>
                    ))}
                  </div>
                  <button onClick={send} disabled={isPending} className="btn-primary" data-testid="send-feedback">
                    <Send className="h-4 w-4" /> Send
                  </button>
                </div>
              </div>
            )}
          </div>

          {selected && !isPending && (
            <div className="hifz-card" data-testid="voice-note-form">
              <h2 className="font-display font-semibold text-lg text-forest-700 flex items-center gap-2">
                <Mic className="h-4 w-4 text-terracotta-600" /> Send a voice note to {selected.name.split(" ")[0]}
              </h2>
              <p className="text-xs text-muted-foreground mt-1">Quick audio &mdash; up to 90 seconds.</p>
              <input
                data-testid="voice-note-text"
                value={voiceNote} onChange={(e) => setVoiceNote(e.target.value)}
                placeholder="Optional caption"
                className="mt-3 w-full rounded-xl border border-sand-300 bg-white px-4 py-2.5 text-sm"
              />
              <div className="mt-3">
                <VoiceRecorder onRecorded={sendVoice} testidPrefix="teacher-vn" />
              </div>
            </div>
          )}

          {sentNotes.length > 0 && (
            <div className="hifz-card">
              <h2 className="font-display font-semibold text-lg text-forest-700">Recently sent voice notes</h2>
              <ul className="mt-3 space-y-2">
                {sentNotes.slice(0, 5).map((n) => (
                  <li key={n.voice_note_id} className="p-3 rounded-xl bg-sand-200/40 border border-sand-300 text-sm" data-testid={`sent-vn-${n.voice_note_id}`}>
                    <div className="font-medium text-forest-700">{n.to_name}</div>
                    {n.note && <div className="text-foreground/75 mt-0.5">{n.note}</div>}
                    <div className="text-[11px] text-muted-foreground mt-1">{new Date(n.created_at).toLocaleString()}</div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
